import { useState } from "react";
import { useRecoilValue } from "recoil";
import {
    Column,
    FilterRow,
    HeaderFilter,
    LoadPanel,
    DataGrid,
    Paging,
    Scrolling,
} from "devextreme-react/data-grid";
import { SelectBox } from "devextreme-react/select-box";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { Pager } from "devextreme-react/tree-list";

import { dataGridConfig } from "../../constants";
import {
    Entity,
    FolderNode,
    UserPerSite,
} from "../../api-client-nswag/custompermission-client";
import { usersNotInEntityByFolderSelector } from "../../recoil/selectors/users.selectors";
import { applyOverflowTooltipOnCellPrepared, renderTruncatedTextCell } from "./manageCustomPermissionsGridHelpers";

interface AssignUserCustomPermissionProps {
    selectedLegalEntities?: Entity | Entity[] | undefined;
    selectedUsers?: UserPerSite[];
    setSelectedUsers?: React.Dispatch<React.SetStateAction<UserPerSite[]>>;
    selectedFolderItem?: FolderNode | undefined;
}

const AssignUserCustomPermission: React.FC<AssignUserCustomPermissionProps> = ({
    selectedLegalEntities,
    selectedUsers,
    setSelectedUsers,
    selectedFolderItem,
}) => {
    const [selectedUserId, setSelectedUserId] = useState<string | number | null>(null);
    const users = useRecoilValue(usersNotInEntityByFolderSelector(selectedFolderItem?.parentFolderId ?? null));
    const isRootEntity = selectedFolderItem?.entityId === 0;

    const handleRemoveUser = (id: string | number) => {
        setSelectedUsers && setSelectedUsers((prev) => prev.filter((u) => u.userId !== id));
    };

    const handleAddUser = (userId: string | number | null | undefined) => {
        if (userId === null || userId === undefined) return;

        const user = users.find((u) => u.userId === userId);
        if (!user) return;
        if (setSelectedUsers) {
            setSelectedUsers((prev) => {
                const already = prev.some((u) => u.userId === user.userId);
                if (already) return prev;
                return [...prev, user];
            });
        }
        setSelectedUserId(null);
    };

    const cellUserNameRender = (cellData: any) => {
        return (
            <span className="hyper-link-blue">
                {cellData?.data?.username}
            </span>
        );
    };

    const cellRenderGroup = (cellData: any) => {
        return <span>{cellData?.data?.userRole}</span>;
    };

    const cellRenderEmail = (cellData: any) => {
        return renderTruncatedTextCell(cellData?.data?.userEmail);
    };

    const handleDataGridCellPrepared = (e: any) => {
        applyOverflowTooltipOnCellPrepared(e);
    };

    const cellRenderActions = (e: any) => {
        const rowData = e.data;

        return (
            <div>
                <RemoveCircleOutlineIcon
                    fontSize="small"
                    className="mcp-action-icon"
                    onClick={() => handleRemoveUser(rowData.userId)}
                />
            </div>
        );
    };


    const subtitleText = isRootEntity
        ? "Select which users you would like to add:"
        : "Select which users you would like to add to the selected legal entities:";

    const noDataText = isRootEntity
        ? "Add Users to the selected folder using the field above."
        : "Add Users to the selected entities using the field above.";

    const selectedEntitiesList: Entity[] = Array.isArray(selectedLegalEntities)
        ? selectedLegalEntities
        : selectedLegalEntities
            ? [selectedLegalEntities]
            : [];


    return (
        <div className="section-wrap">
            {!isRootEntity && <div className="block">
                <span className="block-title">Legal Entites</span>
                <span className="block-subtitle">
                    The following legal entities have been selected:
                </span>
                {selectedEntitiesList.length > 0 && <div className="pill-row">
                    {selectedEntitiesList.map((entity) => (
                        <span key={entity?.entityId} className="chip chip--entity">
                            {entity?.entityName}
                        </span>
                    ))}
                </div>}
            </div>}

            <div className="block">
                <span className="block-title">Users</span>
                <span className="block-subtitle">
                    {subtitleText}
                </span>
                <div>
                    <div className="control-row">
                        <SelectBox
                            dataSource={users}
                            valueExpr="userId"
                            displayExpr="username"
                            value={selectedUserId}
                            placeholder="Enter a name or select from the dropdown list"
                            showClearButton={true}
                            searchEnabled={true}
                            searchMode="contains"
                            onSelectionChanged={({ selectedItem }) => {
                                if (!selectedItem) return;
                                handleAddUser(selectedItem.userId);
                                setSelectedUserId(null);
                            }}
                            dropDownButtonRender={() => {
                                return (
                                    <div className="custom-dropdown-btn">
                                        <span
                                            className="dx-icon dx-icon-chevrondown"
                                        />
                                    </div>
                                );
                            }}
                            width={"100%"}
                        />
                    </div>
                    <div>
                        <DataGrid
                            height={250}
                            {...dataGridConfig}
                            dataSource={selectedUsers}
                            noDataText={noDataText}
                            allowColumnResizing={true}
                            showBorders={true}
                            showColumnLines={false}
                            showRowLines={true}
                            onCellPrepared={handleDataGridCellPrepared}
                        >
                            <Pager visible={false} />
                            <Paging enabled={false} />
                            <Scrolling mode="virtual" />
                            <LoadPanel enabled={false} />
                            <FilterRow visible={true} />
                            <HeaderFilter visible={true} />

                            <Column
                                alignment="left"
                                dataField="username"
                                caption={"Name"}
                                width={300}
                                cellRender={cellUserNameRender}
                            ></Column>
                            <Column
                                alignment="left"
                                dataField="userRole"
                                caption={"Group"}
                                cellRender={cellRenderGroup}
                            ></Column>
                            <Column
                                cssClass="cell-text"
                                alignment="left"
                                dataField="userEmail"
                                caption={"Email ID"}
                                cellRender={cellRenderEmail}
                            ></Column>
                            <Column
                                type="buttons"
                                caption={"Actions"}
                                cellRender={cellRenderActions}
                            />
                        </DataGrid>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssignUserCustomPermission;
